import React, { createContext, useState, useEffect, useMemo, useRef, useContext, useCallback } from 'react';
import { defaultRequiredFields, genAttributes } from '@onward-delivery/core';
import { css } from '@emotion/react';
import { Grid, DialogContent } from '@material-ui/core';
import { useClientUser } from '@/hooks';
import { VerticalTabs, VerticalTab } from '@/components/Tabs';
import {
    ModalTitle,
    SecondaryButton,
    PrimaryButton,
    ResponsiveSidebarDialog,
    StickyModalActions,
} from '@/styles/blocks';
import { hasPhoneWarning } from '@/utilities/validatePhoneNumber';

import PickupTab from './PickupTab';
import ItemsEditTab from './ItemsEditTab';
import ServicesTab from './ServicesTab';
import HaulAwayTab from './HaulAwayTab';
import { useSwappedAttributes } from '../../hooks';
import { useServiceCatalog } from '../../hooks/serviceCatalog';
import { usePcMilerConfig } from '../../hooks/pcMiler';
import {
    middleMileOriginFields,
    useEditModalCallbacks,
    useEditModalTabFields,
    useEditModalDynamicFields,
} from '../../hooks/modal';
import { price, distance } from '../../utilities/processOrders';
import { customerHasServiceDefinitions, isServiceCallbackOrder } from '../../utilities/serviceCallback';
import { getInvalidServiceLines } from '../../utilities/serviceCatalog';
import { getItemErrors } from './ItemsEditTab/utils';
import { EDIT_ORDER_TABS, EDIT_ORDER_TABS_DISPLAY, EDIT_ORDER_TABS_ICON } from '../../constants';
import AdminTab from './AdminTab';
import WarehouseTab from './WarehouseTab';
import NotesTab from './NotesTab';
import NotificationsTab from './NotificationsTab';
import { omit, startCase, uniq } from 'lodash';
import { ORDER_READONLY_FIELDS } from '@onward-delivery/core';
import { useLazyQuery, useMutation, useQuery } from '@apollo/client';
import { INSERT_MANIFEST } from '@/components/Manifests/graphql';
import { captureException } from '@sentry/react';
import OrderDetailsTab from './OrderDetailsTab';
import CustomerTab from './CustomerTab';
import { resolveEditOrderTabs } from './tabs';
import OriginTab from './OriginTab';
import { post } from '@/utilities/onwardClient';
import { NOTIFY_CARRIER_OF_ADMIN_ASSIGNMENT } from '@/constants/apiRoutes';
import { OnwardCheckbox } from '../../blocks';
import { QUERY_ITEM_SKU } from '../../graphql/queries';
import { DELETE_SAVED_CUSTOMER, INSERT_SAVED_CUSTOMER_LOCATION } from '../../graphql/mutations';
import { serviceLinesChanged } from '@/utilities/serviceStopRecalc';
import { useRecalcStopServiceTimes } from '@/hooks';
import { OpenInNew } from '@material-ui/icons';
import { UserContext } from '@/components/App';
import { v4 as uuid } from 'uuid';
import { capitalize } from 'lodash';
import AdminNotes from './AdminTab/AdminNotes';
import AdminRatesTab from './AdminRatesTab';
import { buildServicesSavePayload } from './saveServices';
import { buildDocumentsSavePayload } from './saveDocuments';
import { buildItemsSavePayload } from './saveItems';
import { clearGeocodingLeg, isReviewGeocodingStatus } from './geocoding';
import { assignmentPatch } from './carrierAssignment';
import { QUERY_CARRIER_ASSIGNMENT_BY_ID } from '@/graphql/queries/orders';

export const ModalContext = createContext();

const DEFAULT_CALLBACKS = {
    onError: () => {},
    onSave: () => {},
    onClose: () => {},
};

const DEFAULT_OPTIONS = {
    disableAddressEditing: false,
    enablePickupDateEditing: false,
    enableAdminTestAccounts: false,
    disableGeocoding: false,
    startDirty: true,
    customerAvailabilityToggle: false,
    readOnly: false,
};

const initNote = {
    private: false,
    notify: false,
    note: '',
    type: null,
};

const EditModalContent = () => {
    const [tab, setTab] = useState(0);
    const containerRef = useRef(null);

    const {
        state: { tabs, initTab, initTabOffset, hasErrorTab },
    } = useContext(ModalContext);

    const singleTab = useMemo(() => {
        return tabs.length === 1 && initTab === tabs[0];
    }, [tabs, initTab]);

    const scrollToTab = (tabId) => {
        const children = containerRef.current?.children || [];
        for (let i = 0; i < children.length; i++) {
            const child = children[i];
            if (child.id === tabId) {
                const containerRect = containerRef.current.getBoundingClientRect();
                const elementRect = child.getBoundingClientRect();
                const relativeTop = elementRect.top - containerRect.top;
                const targetScroll = containerRef.current.scrollTop + relativeTop - (initTabOffset || 0);
                containerRef.current.scrollTo({
                    top: targetScroll,
                    behavior: 'smooth',
                });
                break;
            }
        }
    };

    useEffect(() => {
        if (initTab) {
            scrollToTab(initTab);
        }
    }, [initTab]);

    const renderTab = (tab) => {
        let content;

        switch (tab) {
            case EDIT_ORDER_TABS.ORIGIN:
                content = <OriginTab />;
                break;
            case EDIT_ORDER_TABS.PICKUP:
                content = <PickupTab />;
                break;
            case EDIT_ORDER_TABS.CUSTOMER:
                content = <CustomerTab />;
                break;
            case EDIT_ORDER_TABS.ORDER_DETAILS:
                content = <OrderDetailsTab />;
                break;
            case EDIT_ORDER_TABS.ITEMS:
                content = <ItemsEditTab />;
                break;
            case EDIT_ORDER_TABS.SERVICES:
                content = <ServicesTab />;
                break;
            case EDIT_ORDER_TABS.HAUL_AWAY:
                content = <HaulAwayTab />;
                break;
            case EDIT_ORDER_TABS.ADMIN:
                content = <AdminTab />;
                break;
            case EDIT_ORDER_TABS.NOTES:
                content = <NotesTab />;
                break;
            case EDIT_ORDER_TABS.NOTIFICATIONS:
                content = <NotificationsTab />;
                break;
            case EDIT_ORDER_TABS.ADMIN_NOTES:
                content = <AdminNotes />;
                break;
            case EDIT_ORDER_TABS.WAREHOUSE:
                content = <WarehouseTab />;
                break;
            case EDIT_ORDER_TABS.ADMIN_RATES:
                content = <AdminRatesTab />;
                break;
        }
        return content;
    };

    return (
        <>
            {!singleTab ? (
                <VerticalTabs
                    value={tab}
                    onChange={(e, idx) => {
                        const tabId = tabs[idx];
                        scrollToTab(tabId);
                    }}
                >
                    {tabs.map((tab, idx) => {
                        const TabIcon = EDIT_ORDER_TABS_ICON[tab];
                        return (
                            <VerticalTab
                                key={tab}
                                label={EDIT_ORDER_TABS_DISPLAY[tab]}
                                icon={TabIcon ? <TabIcon /> : null}
                                value={idx}
                                error={hasErrorTab[tab]}
                            />
                        );
                    })}
                </VerticalTabs>
            ) : null}
            <Grid
                ref={containerRef}
                css={css`
                    flex-grow: 1;
                    padding: 16px 32px;
                    height: 100%;
                    overflow-y: auto;
                    display: flex;
                    flex-direction: column;
                    flex-wrap: nowrap;
                `}
                onScroll={(e) => {
                    let tabId;
                    const { clientHeight, scrollHeight, scrollTop, children } = e.target;
                    const scrollBottom = scrollTop + clientHeight;

                    if (scrollTop < 100) {
                        const child = children[0];
                        tabId = child.id;
                    } else if (scrollHeight - scrollBottom < 100) {
                        const child = children[children.length - 1];
                        tabId = child.id;
                    } else {
                        const center = scrollTop + clientHeight / 2;
                        let sum = 0;
                        for (let i = 0; i < children.length; i++) {
                            const child = children[i];
                            sum += child.clientHeight;
                            if (sum > center && !!child.id) {
                                tabId = child.id;
                                break;
                            }
                        }
                    }

                    const idx = tabs.indexOf(tabId);
                    if (idx >= 0 && idx !== tab) {
                        setTab(idx);
                    }
                }}
            >
                {tabs.map((tab, i) => (
                    <React.Fragment key={tab}>
                        {i > 0 ? (
                            <Grid
                                item
                                css={css`
                                    width: calc(100% + 64px);
                                    margin-left: -32px;
                                    margin-right: -32px;
                                    margin-top: 10px;
                                `}
                            >
                                <hr
                                    css={css`
                                        margin: 0;
                                        border: none;
                                        border-top: 1px solid black;
                                    `}
                                />
                            </Grid>
                        ) : null}
                        <Grid
                            id={tab}
                            container
                            direction="column"
                            css={css`
                                flex-wrap: nowrap;
                                width: 100%;
                                padding-top: 20px;
                            `}
                        >
                            {renderTab(tab)}
                        </Grid>
                    </React.Fragment>
                ))}
            </Grid>
        </>
    );
};

const EditOrderModal = ({ order, open, initTab, initTabOffset, callbacks, requiredAttributes, tabs, opt }) => {
    const [saveInflight, setIsSaving] = useState(false);
    const [geoCache, setGeocache] = useState({});
    const [overrides, modifyClone] = useState({});
    const [errors, setErrors] = useState({});
    const [isDirty, setDirty] = useState({});
    const [createSingleOrderManifest, setCreateSingleOrderManifest] = useState(null);
    const [sendCarrierAssignedEmail, setSendCarrierAssignedEmail] = useState(false);
    const [saveCustomer, setSaveCustomer] = useState(false);
    const [savedCustomer, setSavedCustomer] = useState(null);
    const [noteInfo, setNoteInfo] = useState(initNote);
    const { client_id, roles, userType, user_id, isOnwardAdmin, isCorporateAdmin, isCarrierAdmin } = useClientUser();
    const { user } = useContext(UserContext);

    const isNewOrder = window.location.pathname === '/import' || window.location.pathname.includes('job');

    const clone = useMemo(() => {
        return { ...order, ...overrides };
    }, [order, overrides]);

    const serviceCatalogFallback = useMemo(
        () => ({
            shipperId: clone?.shipper_id || user_id,
        }),
        [clone?.shipper_id, user_id]
    );
    const {
        serviceCatalog,
        serviceCatalogError,
        loading: serviceCatalogLoading,
        refreshCatalogs: refreshServiceCatalogs,
    } = useServiceCatalog(clone, serviceCatalogFallback);

    // Onward and corporate admins can edit read-only (invoiced) orders. An admin impersonating a
    // client is neither, so they see the client's lock.
    const options = useMemo(() => {
        const merged = { ...DEFAULT_OPTIONS, ...(opt || {}) };
        return { ...merged, readOnly: merged.readOnly && !isOnwardAdmin && !isCorporateAdmin };
    }, [opt, isOnwardAdmin, isCorporateAdmin]);

    const showAdminTab = isOnwardAdmin || options.enableAdminTab;
    const showAdminRatesTab =
        (isCorporateAdmin || isCarrierAdmin) &&
        !showAdminTab &&
        clone?.shipper_rate_override?.marketplace_tier !== 'T3_FULLY_MANAGED';

    const isServiceCallback = useMemo(() => isServiceCallbackOrder(clone), [clone]);
    // Show the SERVICES tab when the customer has service definitions configured (customer
    // config, not whether this specific order carries service lines).
    const showServicesTab = customerHasServiceDefinitions(clone, serviceCatalog);

    const combinedTabs = useMemo(
        () =>
            resolveEditOrderTabs({
                tabs,
                order: clone,
                showAdminTab,
                showAdminRatesTab,
                isServiceCallback,
                showServicesTab,
            }),
        [
            tabs,
            clone.has_middle_mile_origin,
            clone.is_middle_mile,
            clone.carrier_id,
            clone.order_type,
            showAdminTab,
            showAdminRatesTab,
            isServiceCallback,
            showServicesTab,
        ]
    );

    useEffect(() => {
        modifyClone({ ...(order || {}) });
        setDirty({});
    }, [order]);

    // Reset noteInfo when modal closes
    useEffect(() => {
        if (!open) {
            setNoteInfo(initNote);
        }
    }, [open]);

    const { geocode_failed, address } = useSwappedAttributes(clone);
    const { address: pu_address, geocode_failed: pu_geocode_failed } = useSwappedAttributes(clone, true);
    const originAttrs = useMemo(() => genAttributes(clone || {}, 'ORIGIN'), [clone]);

    const { data: skus } = useQuery(QUERY_ITEM_SKU, {
        variables: {
            client_id: clone.shipper_id,
        },
        skip: !clone.shipper_id,
        onError: (err) => {
            console.error(err);
            captureException(err);
        },
    });

    // Ordering is QUERY_ITEM_SKU's job.
    const skusSorted = useMemo(() => (skus?.results || []).map((row) => row.sku), [skus]);

    // When the shipper has PC Miler enabled, distance() on save routes through the backend
    // (no browser SDK for PC Miler); otherwise it uses the browser Google fast path.
    const {
        applies: pcMilerApplies,
        loading: pcMilerLoading,
        error: pcMilerError,
        refetch: refetchPcMiler,
    } = usePcMilerConfig(clone?.shipper_id);

    const pcMilerUnresolved = pcMilerLoading || !!pcMilerError;

    const required = useMemo(() => {
        //custom required attributes
        let baseRequired;
        if (requiredAttributes) {
            if (Array.isArray(requiredAttributes)) {
                baseRequired = requiredAttributes;
            } else if (typeof requiredAttributes === 'function') {
                baseRequired = requiredAttributes(clone);
            }
        }

        // Keep derived geocode/route requirements aligned with the caller. EditModal
        // has custom validation for each field, so calculated async fields pass when
        // the caller disables geocoding or related address fields are dirty.
        if (baseRequired === undefined) {
            baseRequired = defaultRequiredFields(clone, { disableGeocoding: options.disableGeocoding });
        }
        const originRequired =
            clone?.has_middle_mile_origin && combinedTabs.includes(EDIT_ORDER_TABS.ORIGIN)
                ? middleMileOriginFields(originAttrs)
                : [];

        return uniq([...(baseRequired || []), ...originRequired].filter(Boolean));
    }, [requiredAttributes, clone, options.disableGeocoding, combinedTabs, originAttrs]);

    // Derived-field recomputation (order_updated_hash, full_address, tz-shifted dates)
    // runs through the raw setter, separate from field-level dirty tracking.
    useEditModalDynamicFields(clone, modifyClone);

    const modalCallbacks = useEditModalCallbacks(
        {
            geoCache,
            clone,
            errors,
        },
        {
            modifyClone,
            setGeocache,
            setErrors,
            onError: callbacks.onError || DEFAULT_CALLBACKS.onError,
            setDirty,
        }
    );

    const [insertManifest] = useMutation(INSERT_MANIFEST, {
        onError: (error) => {
            console.error(error.message);
            captureException(error);
        },
    });

    // Nulls the containing stops' service_time + flags routes for re-optimize when services change.
    const recalcStopServiceTimes = useRecalcStopServiceTimes(order);

    const [insertSavedCustomer] = useMutation(INSERT_SAVED_CUSTOMER_LOCATION, {
        onError: (error) => {
            console.error(error.message);
            captureException(error);
        },
    });

    const [deleteSavedCustomer] = useMutation(DELETE_SAVED_CUSTOMER, {
        onError: (error) => {
            console.error(error.message);
            captureException(error);
        },
    });

    // Re-reads the columns `carrier/unassign` clears. `network-only` so it reaches Hasura rather
    // than answering from the cache it is there to correct.
    const [fetchCarrierAssignment] = useLazyQuery(QUERY_CARRIER_ASSIGNMENT_BY_ID, {
        fetchPolicy: 'network-only',
        onError: (err) => {
            console.error(err);
            captureException(err);
        },
    });

    // Exposed on the context as `refetchOrder`, which is what AdminTab calls after an unassign.
    //
    // It has to resync the clone itself, not just delegate to the caller: `clone` is only rebuilt
    // when the `order` prop's identity changes, and every caller but OrderDetailsPage holds that
    // order in its own useState, so refetching a page's list query leaves the modal on the old
    // snapshot. Writing the read into the normalized cache is what refreshes the page behind the
    // modal -- `orders` is keyed on order_id -- so callers need not pass anything.
    //
    // The caller's own refetch still runs first when there is one, for the queries a narrow read
    // cannot touch.
    //
    // `leg` scopes the patch to the leg that was actually torn down -- see `assignmentPatch`. It
    // deliberately stops here and is not forwarded: the caller's `refetchOrder` is an Apollo
    // `refetch`, whose first argument is a variables object it merges over the query's own, so a
    // bare string would spread to `{ 0: 'D', 1: 'I', ... }` and corrupt the read.
    const refetchOrder = useCallback(
        async (leg) => {
            await callbacks.refetchOrder?.();

            if (!order?.order_id) return;

            const { data } = await fetchCarrierAssignment({ variables: { order_id: order.order_id } });
            const patch = assignmentPatch(data?.order, leg);
            if (!patch) return;

            // Merged, not replaced: the operator may have edits open in other tabs, and the unassign
            // only owns these columns.
            modifyClone((prev) => ({ ...prev, ...patch }));
        },
        [callbacks.refetchOrder, order?.order_id, fetchCarrierAssignment]
    );

    const fieldsByTab = useEditModalTabFields(clone);

    const hasError = useMemo(() => {
        if (clone) {
            return Object.fromEntries(
                required
                    .filter((field) => {
                        const isEmpty = !clone[field] || clone[field].length === 0;
                        switch (field) {
                            case 'dropoff_lat':
                            case 'dropoff_lng':
                                if (
                                    clone.order_type === 'will_call' ||
                                    (!combinedTabs.includes(EDIT_ORDER_TABS.PICKUP) && clone.order_type === 'return')
                                ) {
                                    return false;
                                }
                                if (clone.dropoff_address_is_custom) {
                                    return isEmpty;
                                }
                                if (clone.dropoff_address_geocode_failed) {
                                    return !isDirty.dropoff_address;
                                }
                                // Saved geocoded orders need coords
                                return clone.order_id ? isEmpty : false;
                            case 'pickup_lat':
                            case 'pickup_lng':
                                if (!combinedTabs.includes(EDIT_ORDER_TABS.PICKUP) && clone.order_type !== 'return') {
                                    return false;
                                }
                                if (clone.pickup_address_is_custom) {
                                    return isEmpty;
                                }
                                if (clone.pickup_address_geocode_failed) {
                                    return !isDirty.pickup_address;
                                }
                                // Saved geocoded orders need coords
                                return clone.order_id ? isEmpty : false;
                            case originAttrs.lat:
                            case originAttrs.long:
                                if (!clone.has_middle_mile_origin || !combinedTabs.includes(EDIT_ORDER_TABS.ORIGIN)) {
                                    return false;
                                }
                                if (isReviewGeocodingStatus(clone.geocoding?.[originAttrs.geocoding]?.status)) {
                                    return true;
                                }
                                return clone.order_id ? isEmpty : false;
                            case 'shipper_rate':
                            case 'carrier_rate':
                                return false;
                            case 'itemsByOrderId':
                                return clone.order_type === 'service' ? false : isEmpty;
                            case 'duration_seconds':
                            case 'distance':
                            case 'miles':
                                if (clone.order_type === 'will_call') {
                                    return false;
                                }
                                if (clone.distance_failed || clone[field] === 0) {
                                    return !(isDirty.pickup_address || isDirty.dropoff_address);
                                }
                                // distance/miles/duration_seconds are derived route metrics
                                // recomputed by the backend on save. In disableGeocoding mode
                                // they can't be fixed client-side, so flagging would lock Save
                                // forever — skip the check then.
                                return options.disableGeocoding ? false : isEmpty;
                            // Phone never blocks Save. An unparseable number surfaces through
                            // hasWarning below so it is visible without locking the order.
                            case 'pickup_phone':
                            case 'dropoff_phone':
                                return false;
                            case 'preferred_delivery_date':
                            case 'alternative_delivery_dates':
                                return false;
                            case 'warehouse_delivery_status':
                            case 'warehouse_estimated_delivery_date':
                                return clone.is_middle_mile && isEmpty;

                            default:
                                return isEmpty;
                        }
                    })
                    .map((field) => [field, true])
            );
        }
        return {};
    }, [
        clone,
        required,
        options,
        isDirty,
        address,
        pu_address,
        geocode_failed,
        pu_geocode_failed,
        combinedTabs,
        originAttrs,
    ]);

    // Non-blocking counterpart to hasError: every *_phone field the order carries whose
    // value is present but unparseable. Keyed by field so the contact rows can highlight
    // exactly the input that needs attention.
    const hasWarning = useMemo(() => {
        if (!clone) return {};

        return Object.fromEntries(
            Object.keys(clone)
                .filter((field) => field.endsWith('_phone') && hasPhoneWarning(clone[field]))
                .map((field) => [field, true])
        );
    }, [clone]);

    const onSavedCustomerSelect = useCallback(
        (customer) => {
            // genAttributes returns the per-side key map (dropoff_* vs pickup_*). Keep this
            // destructure in lockstep with the mappedCustomerFields block below.
            const { address, city, state, street, zip, lat, long, unit, location, location_type, geocoding } =
                genAttributes(clone);

            // Map customer fields to order fields
            const mappedCustomerFields = {
                [address]: customer?.address,
                [city]: customer?.business_city,
                [state]: customer?.business_state,
                [street]: customer?.business_address,
                [zip]: customer?.business_zip,
                [lat]: customer?.lat,
                [long]: customer?.lng,
                [unit]: customer?.business_unit,
                [location]: customer?.location_type,
                [location_type]: customer?.location_info,
                dropoff_name: customer?.contact_name,
                dropoff_phone: customer?.contact_phone,
                dropoff_email: customer?.contact_email,
                geocoding: clearGeocodingLeg(clone.geocoding, geocoding),
            };

            modalCallbacks.modifyOrder({
                ...mappedCustomerFields,
            });
            setSavedCustomer(customer);
        },
        [clone, modalCallbacks]
    );

    // Aggregate only — each row derives its own errors from the same helper so it can stay memoized.
    const itemHasError = useMemo(() => {
        if (!clone) return [];

        return (clone.itemsByOrderId || []).map((item) =>
            getItemErrors(item, {
                oms: clone.oms,
                freightType: clone.freight_type,
                sourceForm: clone.source_form,
                assemblyRequired: clone.assembly_required,
            })
        );
    }, [clone]);

    const adminHasError = useMemo(() => {
        const isAdmin = combinedTabs.includes(EDIT_ORDER_TABS.ADMIN);
        const isAdminRates = combinedTabs.includes(EDIT_ORDER_TABS.ADMIN_RATES);

        if (isAdmin || isAdminRates) {
            const priceOverrideFields = [
                'admin_shipper_rate_override',
                'admin_carrier_rate_override',
                'admin_middle_mile_shipper_rate_override',
                'admin_middle_mile_carrier_rate_override',
                'order_revenue_adjustments',
            ];

            const priceOverrideChanged = priceOverrideFields.some(
                (attr) => !!overrides?.[attr] && overrides?.[attr] !== order?.[attr]
            );

            const hasNewInvoicingNote = (clone?.notes || []).some((n) => !n?.created_at && n?.type === 'Invoicing');

            if (isAdmin) {
                const dropoffBeforePickup =
                    clone.del_window_start &&
                    clone.pickup_window_end &&
                    new Date(clone.del_window_start) < new Date(clone.pickup_window_start);

                return {
                    delivery_date: dropoffBeforePickup,
                    pickup_date: dropoffBeforePickup,
                    delivery_window:
                        clone.del_window_start &&
                        clone.del_window_end &&
                        new Date(clone.del_window_start) > new Date(clone.del_window_end),
                    pickup_window:
                        clone.pickup_window_start &&
                        clone.pickup_window_end &&
                        new Date(clone.pickup_window_start) > new Date(clone.pickup_window_end),
                    rate_change_note: priceOverrideChanged && !hasNewInvoicingNote,
                };
            }

            return {
                rate_change_note: priceOverrideChanged && !hasNewInvoicingNote,
            };
        }
        return {};
    }, [clone, overrides, noteInfo, order]);

    // Service line(s) carrying a code the catalog doesn't offer. Catalog validity isn't a
    // field, so it's tracked separately from `hasError` and folded into the tab error + Save
    // gate. Unconditional (not dirty-gated) so an invalid code reads red on open.
    const serviceHasError = useMemo(
        () => getInvalidServiceLines(clone, serviceCatalog).length > 0,
        [clone, serviceCatalog]
    );

    const referenceHasError = useMemo(() => {
        const refs = clone?.references || [];
        const trimmedKeys = refs.map((r) => (r.key || '').trim()).filter(Boolean);
        const hasBlank = refs.some((r) => !(r.key || '').trim() || !(r.value || '').trim());
        const hasDupKey = new Set(trimmedKeys).size !== trimmedKeys.length;
        return hasBlank || hasDupKey;
    }, [clone]);

    const hasErrorTab = useMemo(() => {
        let errorObject = Object.fromEntries(
            Object.keys(fieldsByTab).map((tab) => {
                if (tab === EDIT_ORDER_TABS.ITEMS) {
                    return [
                        tab,
                        fieldsByTab[tab].some((field) => hasError[field] && (options.startDirty || isDirty[field])) ||
                            itemHasError.some((item, idx) => {
                                return Object.entries(item).some(([key, isInvalid]) => {
                                    return isInvalid && (options.startDirty || isDirty.itemsByOrderId?.[idx]?.[key]);
                                });
                            }),
                    ];
                }

                return [
                    tab,
                    fieldsByTab[tab].some((field) => hasError[field] && (options.startDirty || isDirty[field])),
                ];
            })
        );

        if (adminHasError.rate_change_note) {
            errorObject[EDIT_ORDER_TABS.ADMIN_NOTES] = true;
        }

        // Always flag the SERVICES tab — that's where the invalid line renders and the Add
        // Service catalog lives, even though genAttributesPerTab buckets the services field
        // under ITEMS.
        if (serviceHasError) {
            errorObject[EDIT_ORDER_TABS.SERVICES] = true;
        }

        // References render under the ORDER_DETAILS tab.
        if (referenceHasError) {
            errorObject[EDIT_ORDER_TABS.ORDER_DETAILS] = true;
        }

        return errorObject;
    }, [
        hasError,
        itemHasError,
        fieldsByTab,
        isDirty,
        adminHasError,
        serviceHasError,
        referenceHasError,
        options.startDirty,
    ]);

    const isValid = useMemo(() => {
        return (
            !Object.values(hasError).some((isInvalid) => isInvalid) &&
            !Object.values(itemHasError).some((item) => Object.values(item).some((isInvalid) => isInvalid)) &&
            !Object.values(adminHasError).some((isInvalid) => isInvalid) &&
            !serviceHasError &&
            !referenceHasError
        );
    }, [hasError, itemHasError, adminHasError, serviceHasError, referenceHasError]);

    const orderLabel = useMemo(() => {
        switch (clone.order_type) {
            case 'will_call':
                return 'Will Call';
            case 'wh_inventory':
                return 'Inventory';
            case 'service':
                return 'Service Callback';
            default:
                return startCase(clone.order_type || 'delivery');
        }
    }, [clone]);

    const linkAttrs = [
        { meta: 'middle_mile_integration_meta', type: 'middle_mile_integration_type' },
        { meta: 'dispatch_integration_meta', type: 'dispatch_integration_type' },
    ];

    // Memoize the ModalContext value so memo()-wrapped consumers (ItemsEditTab) can skip
    // renders; without it every parent render builds a fresh value and busts the memo.
    // MUST stay above the `if (!order) return null` early return — a conditional hook would
    // violate React's hook-ordering rule.
    const modalContextValue = useMemo(
        () => ({
            state: {
                skus: skusSorted,
                order: clone,
                serviceCatalog,
                serviceCatalogError,
                serviceCatalogLoading,
                // The shipper whose catalog was loaded (the order's, else the user's) — the one a
                // quick add writes to and refreshes.
                serviceCatalogShipperId: serviceCatalogFallback.shipperId,
                tabs: combinedTabs,
                options,
                initTab,
                initTabOffset,
                hasError,
                hasWarning,
                adminHasError,
                hasErrorTab,
                errors,
                isDirty,
                createSingleOrderManifest,
                setCreateSingleOrderManifest,
                sendCarrierAssignedEmail,
                setSendCarrierAssignedEmail,
                savedCustomer,
                setSaveCustomer,
                noteInfo,
            },
            callbacks: {
                ...DEFAULT_CALLBACKS,
                ...callbacks,
                ...modalCallbacks,
                onSavedCustomerSelect,
                deleteSavedCustomer,
                setNoteInfo,
                // After the spread on purpose: it wraps the caller's `refetchOrder` rather than
                // being replaced by it.
                refetchOrder,
                refreshServiceCatalogs,
            },
        }),
        [
            skusSorted,
            clone,
            serviceCatalog,
            serviceCatalogError,
            serviceCatalogLoading,
            serviceCatalogFallback.shipperId,
            combinedTabs,
            options,
            initTab,
            initTabOffset,
            hasError,
            hasWarning,
            adminHasError,
            hasErrorTab,
            errors,
            isDirty,
            createSingleOrderManifest,
            sendCarrierAssignedEmail,
            savedCustomer,
            noteInfo,
            callbacks,
            modalCallbacks,
            onSavedCustomerSelect,
            deleteSavedCustomer,
            refetchOrder,
            refreshServiceCatalogs,
        ]
    );

    if (!order) {
        return null;
    }

    return (
        <ResponsiveSidebarDialog
            open={open}
            onClose={callbacks.onClose}
            fullWidth={true}
            maxWidth="lg"
            css={css`
                .MuiDialog-container {
                    align-items: normal;
                }
            `}
        >
            <ModalTitle
                title={options.title || `Order ${clone.order_number || ''} - ${orderLabel}`}
                onClose={callbacks.onClose}
                actions={[{ Icon: OpenInNew, onClick: () => window.open(`/order/${order.order_id}`, '_blank') }]}
                links={[
                    { type: 'Customer', label: order.order_number, link: `/customer/track/${order.order_id}` },
                    ...linkAttrs
                        .filter(({ type, meta }) => order[type] && order[meta]?.tracking_number)
                        .map(({ type, meta }) => ({
                            type: order[type],
                            label: order[meta].tracking_number,
                            link: order[meta].tracking_link,
                        })),
                ]}
            />
            <ModalContext.Provider value={modalContextValue}>
                <DialogContent
                    css={css`
                        width: 100%;
                        padding: 0;
                        display: flex;
                        flex-direction: row;
                    `}
                >
                    <EditModalContent />
                </DialogContent>
            </ModalContext.Provider>
            {order.order_status === 'on_hold_awaiting_payment' && (
                <StickyModalActions border="true">
                    <Grid container direction="row" justifyContent="center">
                        <Grid
                            item
                            css={css`
                                color: red;
                            `}
                        >
                            Order is on hold pending payment authorization from customer. You can continue to make edits
                            and the price will automatically adjust, but the order status cannot be updated until
                            payment is authorized.
                        </Grid>
                    </Grid>
                </StickyModalActions>
            )}
            {showAdminTab && order.payment_status === 'CAPTURED' && order.source_form === 'CUSTOMER' && (
                <StickyModalActions border="true">
                    <Grid container direction="row" justifyContent="center">
                        <Grid
                            item
                            css={css`
                                color: red;
                            `}
                        >
                            Payment has already been captured for this customer shipment form order. Further edits will
                            not result in price changing and could lead to a loss of revenue.
                        </Grid>
                    </Grid>
                </StickyModalActions>
            )}
            {pcMilerError && (
                <StickyModalActions border="true">
                    <Grid container direction="row" justifyContent="center" alignItems="center">
                        <Grid
                            item
                            css={css`
                                color: red;
                            `}
                        >
                            Couldn't load this customer's mileage settings, so saving would risk pricing the order off
                            the wrong mileage engine. Retry before saving.
                        </Grid>
                        <Grid item>
                            <SecondaryButton
                                onClick={() => {
                                    refetchPcMiler?.().catch((err) => console.error(err));
                                }}
                            >
                                Retry
                            </SecondaryButton>
                        </Grid>
                    </Grid>
                </StickyModalActions>
            )}
            <StickyModalActions border="true">
                <Grid>
                    <SecondaryButton disabled={saveInflight} onClick={callbacks.onClose}>
                        Cancel
                    </SecondaryButton>
                </Grid>
                <Grid>
                    {isNewOrder && (
                        <OnwardCheckbox
                            css={css`
                                padding-right: 20px;
                            `}
                            checked={saveCustomer}
                            onChange={(e) => {
                                setSaveCustomer(e.target.checked);
                            }}
                            label="Save Customer"
                        />
                    )}
                    {isValid && !pcMilerUnresolved ? (
                        <PrimaryButton
                            loading={saveInflight}
                            disabled={saveInflight || options.readOnly}
                            onClick={async () => {
                                setIsSaving(true);
                                // Always re-run distance + price on save. `disableGeocoding`
                                // only tells the autocomplete/map not to re-geocode; it does NOT
                                // mean "skip pricing". distance() and price() run on the order's
                                // existing lat/lng, so neither conflicts with a manual pin.
                                let priced = clone;
                                let failed;
                                const [_, [distancedOrder]] = await distance([clone], {
                                    pcMiler: { applies: pcMilerApplies },
                                });
                                [failed, [priced]] = await price([distancedOrder]);
                                const failedPricing = failed[order.key || order.order_id];

                                // Guard the items diff against undefined `itemsByOrderId` on
                                // either side — service-callback orders carry [] today.
                                const cloneItems = clone.itemsByOrderId || [];
                                const removed = (order.itemsByOrderId || [])
                                    .filter((item) => !cloneItems.map((item) => item.item_id).includes(item.item_id))
                                    .map((item) => item.item_id);

                                const notes = priced?.notes || [];

                                // Read before omitting below so they're not lost
                                const cloneDocuments = priced?.documents;
                                const email_subscriptions = priced?.email_subscriptions || [];

                                const {
                                    itemsByOrderId,
                                    tags = [],
                                    services: pricedServices,
                                    ...modified
                                } = omit(priced, ORDER_READONLY_FIELDS);

                                const { items: modifiedItems, itemUpdates } = buildItemsSavePayload({
                                    originalItems: order.itemsByOrderId || [],
                                    editedItems: itemsByOrderId || [],
                                });

                                const modifiedTags = (tags || []).map((mapping) => {
                                    const { __typename, order, service_level, tag, ...rest } = mapping;
                                    return rest;
                                });

                                // orderIds keys the mapping deletes in UPSERT_ORDERS. It comes back
                                // empty when the order arrived without a `documents` projection, so
                                // an unknown list cannot be mistaken for "detach everything".
                                const { documents: modifiedDocuments, orderIds } = buildDocumentsSavePayload({
                                    documents: cloneDocuments,
                                    orderId: clone.order_id,
                                });

                                const removedTags = (order.tags || [])
                                    .filter(
                                        (mapping) => !modifiedTags.map(({ tag_id }) => tag_id).includes(mapping.tag_id)
                                    )
                                    .map((mapping) => mapping.mapping_id);

                                const { services: modifiedServices, servicesRemoved } = buildServicesSavePayload({
                                    pricedServices,
                                    cloneServices: clone.services,
                                    orderServices: order.services,
                                    orderId: clone.order_id,
                                });

                                const cloneRefs = clone.references || [];
                                const modifiedReferences = cloneRefs.map((ref) => {
                                    const clean = omit(ref, ['__typename', 'created_at', 'last_modified_at']);
                                    // New rows omit reference_id so Hasura generates one.
                                    if (!clean.reference_id) {
                                        delete clean.reference_id;
                                    }
                                    return { ...clean, order_id: clone.order_id };
                                });
                                const keptRefIds = new Set(cloneRefs.map((r) => r.reference_id).filter(Boolean));
                                const referencesRemoved = (order.references || [])
                                    .map((r) => r.reference_id)
                                    .filter((id) => id && !keptRefIds.has(id));

                                const newNotes = [...notes];
                                if (noteInfo.note) {
                                    if (roles?.['ADMIN']) {
                                        const newNote = {
                                            note: noteInfo.note,
                                            source_user_type: 'Admin',
                                            source_user_id: user.user_id,
                                            is_acknowledgement: false,
                                            requires_acknowledgement: false,
                                            acknowledged_by_shipper: false,
                                            acknowledged_by_admin: false,
                                            private_to: 'Admin',
                                            type: noteInfo.type || null,
                                            order_id: clone.order_id,
                                            note_id: uuid(),
                                        };
                                        newNotes.push(newNote);
                                    } else {
                                        let source_user_type = 'Shipper';
                                        if (!isNewOrder && clone?.carrier_id && client_id !== clone?.shipper_id) {
                                            source_user_type = 'Carrier';
                                        }
                                        const newNote = {
                                            note: noteInfo.note,
                                            source_user_type: source_user_type,
                                            source_user_id: user_id,
                                            is_acknowledgement: false,
                                            requires_acknowledgement: noteInfo.notify,
                                            acknowledged_by_shipper: false,
                                            acknowledged_by_admin: false,
                                            private_to: noteInfo.private ? capitalize(userType) : null,
                                            type: noteInfo.type,
                                            note_id: uuid(),
                                        };
                                        newNotes.push(newNote);
                                    }
                                    setNoteInfo(initNote);
                                }

                                const modifiedNotes = (newNotes || []).map((note) => {
                                    const { __typename, order_id, user, ...rest } = note;
                                    return rest;
                                });

                                if (saveCustomer) {
                                    const {
                                        address,
                                        city,
                                        state,
                                        street,
                                        zip,
                                        lat,
                                        long,
                                        unit,
                                        location,
                                        location_type,
                                    } = genAttributes(clone);

                                    insertSavedCustomer({
                                        variables: {
                                            customer: {
                                                client_id: client_id,
                                                address: clone?.[address] || null,
                                                business_city: clone?.[city] || null,
                                                business_state: clone?.[state] || null,
                                                business_address: clone?.[street] || null,
                                                business_zip: clone?.[zip] || null,
                                                lat: clone?.[lat] || null,
                                                lng: clone?.[long] || null,
                                                business_unit: clone?.[unit] || null,
                                                location_type: clone?.[location] || null,
                                                location_info: clone?.[location_type] || null,
                                                contact_name: clone?.dropoff_name || null,
                                                contact_phone: clone?.dropoff_phone || null,
                                                contact_email: clone?.dropoff_email || null,
                                                is_customer_location: true,
                                            },
                                        },
                                    });
                                    setSaveCustomer(false);
                                }

                                const subscriptions = email_subscriptions.map((subscription) => {
                                    return {
                                        order_id: clone.order_id,
                                        event: subscription.event,
                                        email: subscription.email,
                                    };
                                });

                                await callbacks.onSave({
                                    errors: {
                                        ...errors,
                                        hasMissingField: [],
                                        failedPricing: failedPricing,
                                    },
                                    order: {
                                        ...modified,
                                        notes: modifiedNotes,
                                    },
                                    items: modifiedItems,
                                    itemsRemoved: removed,
                                    itemUpdates,
                                    tags: modifiedTags,
                                    tagsRemoved: removedTags,
                                    documents: modifiedDocuments,
                                    orderIds,
                                    subscriptions,
                                    services: modifiedServices,
                                    servicesRemoved,
                                    references: modifiedReferences,
                                    referencesRemoved,
                                });

                                // If services changed, null stop service time and flag routes to re-optimize.
                                // The order is already saved at this point, so a failure here means the
                                // stop keeps its stale service_time — surface it instead of failing quietly.
                                if (
                                    serviceLinesChanged({
                                        original: order.services,
                                        modified: modifiedServices,
                                        removed: servicesRemoved,
                                    })
                                ) {
                                    const recalcOk = await recalcStopServiceTimes();
                                    if (!recalcOk) {
                                        callbacks.onError?.(
                                            'Services were saved, but the route stop times could not be refreshed. Please re-optimize the route.'
                                        );
                                    }
                                }

                                if (createSingleOrderManifest) {
                                    await insertManifest({
                                        variables: {
                                            manifest: createSingleOrderManifest,
                                        },
                                    });

                                    setCreateSingleOrderManifest(null);
                                }

                                if (sendCarrierAssignedEmail && modified.carrier_id !== order.carrier_id) {
                                    const assignedResponse = await post(NOTIFY_CARRIER_OF_ADMIN_ASSIGNMENT, {
                                        order: order,
                                        new_carrier_id: modified.carrier_id,
                                    });
                                    setSendCarrierAssignedEmail(false);
                                }

                                setIsSaving(false);
                            }}
                        >
                            Done
                        </PrimaryButton>
                    ) : (
                        <PrimaryButton disabled={true}>Done</PrimaryButton>
                    )}
                </Grid>
            </StickyModalActions>
        </ResponsiveSidebarDialog>
    );
};

export default EditOrderModal;
